import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  InputBase,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowLeft, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { isValid, parse, parseISO } from 'date-fns';

import {
  deleteNutritionistProfilePhoto,
  getCurrentUser,
  getNutritionistPracticeInfo,
  getNutritionistProfilePreferences,
  MAX_CLINIC_LOGO_FILE_BYTES,
  saveNutritionistProfilePreferences,
  updateNutritionistPracticeInfo,
  updateStoredUserProfile,
  uploadNutritionistClinicLogo,
  uploadNutritionistProfilePhoto,
  type NutritionistProfilePreferences,
  type NutritionistProfilePreferencesPayload,
} from '@/lib/api/auth';
import { getClientsDashboard } from '@/lib/api/clients';
import { AppOutlinedTextField, appOutlinedTextFieldSx } from '@/components/shared/AppOutlinedTextField';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { countryNameToCode } from '@/utils/country-code-mapping';
import { getCurrencyForCountry } from '@/utils/countryCurrency';
import { countryCodeToDialCode, countryToPhoneCode } from '@/utils/country-phone-mapping';
import countryOptionsData from '@/data/countries.json';
import layer1Graphic from '@/assets/images/layer-1.svg';
import { PROFESSIONAL_QUALIFICATION_OPTIONS } from '@/constants/professional-qualifications';

interface CountryOption {
  value: string;
  label: string;
}

interface ProfileDetailsFormState {
  firstName: string;
  lastName: string;
  displayName: string;
  pronouns: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  zipCode: string;
  linkedInAccount: string;
  instagramAccount: string;
  professionalBio: string;
  primaryQualification: string;
  specialization: string;
  registrationNumber: string;
  registrationExpiry: string;
  registrationBody: string | null;
}

interface PracticeInfoFormState {
  practiceName: string;
}

interface PracticePersistedFields {
  practiceAddress: string;
  timezone: string;
  country: string;
  zipCode: string;
}

const COUNTRY_OPTIONS: CountryOption[] = countryOptionsData as CountryOption[];

const PRONOUN_OPTIONS = ['She/Her', 'He/Him', 'They/Them', 'Prefer not to say'];
const SECTION_HEADER_FONT_SIZE = '0.9rem';
const MONTH_INDEX_BY_NAME: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};
const COUNTRY_VALUE_BY_ISO_CODE = Object.entries(countryNameToCode).reduce<Record<string, string>>((acc, [countryValue, isoCode]) => {
  acc[isoCode] = countryValue;
  return acc;
}, {});

const FIELD_PLACEHOLDERS = {
  firstName: 'Enter first name',
  lastName: 'Enter last name',
  displayName: 'Enter display name',
  email: 'Enter your email',
  phoneNumber: 'Enter your phone number',
  zipCode: 'Enter Zip code',
  linkedInAccount: 'https://www.linkedin.com/in/your-profile',
  instagramAccount: 'https://www.instagram.com/your-profile',
  professionalBio: 'Enter your professional bio',
  primaryQualification: 'Enter professional qualification',
  specialization: 'Enter specialization',
  registrationNumber: 'Enter registration number',
  practiceName: 'Enter practice / clinic name',
};

const buildPracticeInfoDefaults = (): PracticeInfoFormState => ({
  practiceName: '',
});

const buildPracticePersistedDefaults = (): PracticePersistedFields => ({
  practiceAddress: '',
  timezone: '',
  country: '',
  zipCode: '',
});

const buildDefaults = (): ProfileDetailsFormState => ({
  firstName: '',
  lastName: '',
  displayName: '',
  pronouns: '',
  email: '',
  phoneCode: '+91',
  phoneNumber: '',
  country: '',
  zipCode: '',
  linkedInAccount: '',
  instagramAccount: '',
  professionalBio: '',
  primaryQualification: '',
  specialization: '',
  registrationNumber: '',
  registrationExpiry: '',
  registrationBody: null,
});

const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return 'VD';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

type LooseRecord = Record<string, unknown>;

const asRecord = (value: unknown): LooseRecord | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as LooseRecord;
};

const getNestedRecord = (record: LooseRecord, keys: string[]): LooseRecord | null => {
  for (const key of keys) {
    const nested = asRecord(record[key]);
    if (nested) {
      return nested;
    }
  }
  return null;
};

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

const readNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
};

const readSpecialization = (...values: unknown[]): string => {
  for (const value of values) {
    if (Array.isArray(value)) {
      const joined = value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim())).join(', ');
      if (joined) {
        return joined;
      }
    }
    const singleValue = readString(value);
    if (singleValue) {
      return singleValue;
    }
  }
  return '';
};

const normalizeCountryValue = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) return '';

  const lowerValue = normalized.toLowerCase();
  if (COUNTRY_OPTIONS.some((countryOption) => countryOption.value === lowerValue)) {
    return lowerValue;
  }

  const upperValue = normalized.toUpperCase();
  if (COUNTRY_VALUE_BY_ISO_CODE[upperValue]) {
    return COUNTRY_VALUE_BY_ISO_CODE[upperValue];
  }

  const labelMatch = COUNTRY_OPTIONS.find((countryOption) => countryOption.label.toLowerCase() === lowerValue);
  return labelMatch?.value || '';
};

const getCountryForApi = (countryValue: string): string => {
  if (!countryValue.trim()) return '';
  return countryNameToCode[countryValue] || countryValue.trim().toUpperCase();
};

const getPhoneCodeForCountry = (countryValue: string): string | null => {
  const iso2 = countryToPhoneCode[countryValue];
  if (!iso2) return null;
  const dialCode = countryCodeToDialCode[iso2];
  return dialCode ? `+${dialCode}` : null;
};

const normalizePhoneCode = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) return '+91';
  return normalized.startsWith('+') ? normalized : `+${normalized}`;
};

const padDatePart = (value: number): string => String(value).padStart(2, '0');

const formatUtcDate = (year: number, monthIndex: number, day: number): string | null => {
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== monthIndex
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${year}-${padDatePart(monthIndex + 1)}-${padDatePart(day)}T00:00:00.000Z`;
};

const normalizeRegistrationExpiryForApi = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) return '';

  const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (isoDateMatch) {
    return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}T00:00:00.000Z`;
  }

  const dayMonthNameMatch = normalized.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (dayMonthNameMatch) {
    const day = Number(dayMonthNameMatch[1]);
    const monthIndex = MONTH_INDEX_BY_NAME[dayMonthNameMatch[2].toLowerCase()];
    const year = Number(dayMonthNameMatch[3]);
    if (monthIndex !== undefined) {
      return formatUtcDate(year, monthIndex, day) || normalized;
    }
  }

  const monthNameDayMatch = normalized.match(/^([a-zA-Z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (monthNameDayMatch) {
    const monthIndex = MONTH_INDEX_BY_NAME[monthNameDayMatch[1].toLowerCase()];
    const day = Number(monthNameDayMatch[2]);
    const year = Number(monthNameDayMatch[3]);
    if (monthIndex !== undefined) {
      return formatUtcDate(year, monthIndex, day) || normalized;
    }
  }

  const numericDateMatch = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (numericDateMatch) {
    const firstPart = Number(numericDateMatch[1]);
    const secondPart = Number(numericDateMatch[2]);
    const year = Number(numericDateMatch[3]);
    const month = firstPart > 12 ? secondPart : firstPart;
    const day = firstPart > 12 ? firstPart : secondPart;
    return formatUtcDate(year, month - 1, day) || normalized;
  }

  return normalized;
};

const normalizeRegistrationExpiryForForm = (value: string): string => {
  const normalized = value.trim();
  const isoDateMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})T/);
  return isoDateMatch?.[1] || normalized;
};

const parseRegistrationExpiryToDate = (value: string): Date | null => {
  const normalized = value.trim();
  if (!normalized) return null;

  const isoPrefix = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) {
    const parsed = parseISO(isoPrefix[1]);
    return isValid(parsed) ? parsed : null;
  }

  const dayMonthYear = normalized.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (dayMonthYear) {
    const parsed = parse(
      `${dayMonthYear[1]} ${dayMonthYear[2]} ${dayMonthYear[3]}`,
      'd MMMM yyyy',
      new Date(),
    );
    return isValid(parsed) ? parsed : null;
  }

  const monthDayYear = normalized.match(/^([a-zA-Z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (monthDayYear) {
    const withComma = parse(
      `${monthDayYear[1]} ${monthDayYear[2]}, ${monthDayYear[3]}`,
      'MMMM d, yyyy',
      new Date(),
    );
    if (isValid(withComma)) return withComma;
    const withoutComma = parse(
      `${monthDayYear[1]} ${monthDayYear[2]} ${monthDayYear[3]}`,
      'MMMM d yyyy',
      new Date(),
    );
    return isValid(withoutComma) ? withoutComma : null;
  }

  const numericDateMatch = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (numericDateMatch) {
    const firstPart = Number(numericDateMatch[1]);
    const secondPart = Number(numericDateMatch[2]);
    const year = Number(numericDateMatch[3]);
    const month = firstPart > 12 ? secondPart : firstPart;
    const day = firstPart > 12 ? firstPart : secondPart;
    const parsed = new Date(year, month - 1, day);
    return isValid(parsed) ? parsed : null;
  }

  return null;
};

const toSpecializationsPayload = (value: string): string[] => {
  return value
    .split(',')
    .map((specialization) => specialization.trim())
    .filter(Boolean);
};

const SOCIAL_ACCOUNT_MAX_LENGTH = 500;

const toNullableContactField = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const getSocialAccountFieldErrors = (state: ProfileDetailsFormState): string[] => {
  const errors: string[] = [];
  if (state.linkedInAccount.trim().length > SOCIAL_ACCOUNT_MAX_LENGTH) {
    errors.push('LinkedIn account must be 500 characters or fewer');
  }
  if (state.instagramAccount.trim().length > SOCIAL_ACCOUNT_MAX_LENGTH) {
    errors.push('Instagram account must be 500 characters or fewer');
  }
  return errors;
};

const getQualificationOptionsForValue = (value: string): string[] => {
  const currentValue = value.trim();
  if (
    currentValue &&
    !PROFESSIONAL_QUALIFICATION_OPTIONS.some((option) => option.toLowerCase() === currentValue.toLowerCase())
  ) {
    return [...PROFESSIONAL_QUALIFICATION_OPTIONS, currentValue];
  }
  return [...PROFESSIONAL_QUALIFICATION_OPTIONS];
};

const getNameFromForm = (formState: ProfileDetailsFormState): string => {
  return formState.displayName.trim() || [formState.firstName, formState.lastName].map((part) => part.trim()).filter(Boolean).join(' ');
};

const formatExperienceYearsDisplay = (draft: string): string => {
  const trimmed = draft.trim();
  if (!trimmed) return '';
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n < 0) return '';
  return `${n} ${n === 1 ? 'Year' : 'Years'}`;
};

const parseExperienceYearsForApi = (draft: string): number | undefined => {
  const trimmed = draft.trim();
  if (!trimmed) return undefined;
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
};

/** Mandatory on save except pronouns, professional bio, credentials, and experience years. */
const getMissingMandatoryProfileDetailFields = (state: ProfileDetailsFormState): string[] => {
  const missing: string[] = [];
  if (!state.firstName.trim()) missing.push('First name');
  if (!state.lastName.trim()) missing.push('Last name');
  if (!state.displayName.trim()) missing.push('Display name');
  if (!state.email.trim()) missing.push('Email');
  if (!state.phoneNumber.trim()) missing.push('Phone number');
  if (!state.country.trim()) missing.push('Country');
  if (!state.zipCode.trim()) missing.push('Zip code');
  return missing;
};

const StatsCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.3)',
      px: { xs: 1.05, sm: 1.4 },
      py: 1.05,
      minHeight: 46,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: 0.35,
    }}
  >
    <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.75rem' }, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.33rem', lineHeight: 1 }}>{value}</Typography>
  </Box>
);

const ProfileSection: React.FC<{ title: string; children: React.ReactNode; titleFontSize?: string }> = ({
  title,
  children,
  titleFontSize = SECTION_HEADER_FONT_SIZE,
}) => (
  <Box
    sx={{
      borderRadius: '14px',
      border: 'none',
      bgcolor: '#fff',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      px: { xs: 2.25, sm: 3.5 },
      py: { xs: 2.75, sm: 3 },
    }}
  >
    <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: titleFontSize, lineHeight: 1.05, mb: 2.75 }}>{title}</Typography>
    {children}
  </Box>
);

const ProfileDetailsView: React.FC = () => {
  const navigate = useNavigate();
  const initialUser = useMemo(() => getCurrentUser(), []);
  const [userName, setUserName] = useState(
    initialUser?.fullName?.trim() || initialUser?.email?.split('@')?.[0] || 'User',
  );
  const [formState, setFormState] = useState<ProfileDetailsFormState>(() => buildDefaults());
  const [practiceFormState, setPracticeFormState] = useState<PracticeInfoFormState>(() => buildPracticeInfoDefaults());
  const [practicePersistedFields, setPracticePersistedFields] = useState<PracticePersistedFields>(() => buildPracticePersistedDefaults());
  const [savedProfileImageUrl, setSavedProfileImageUrl] = useState(initialUser?.profileImageUrl || '');
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [profileImageName, setProfileImageName] = useState('');
  const [selectedProfileImageFile, setSelectedProfileImageFile] = useState<File | null>(null);
  const [isProfilePhotoRemoved, setIsProfilePhotoRemoved] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState('');
  const [logoFileName, setLogoFileName] = useState('');
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const isPageBusy = isPageLoading || isSavingProfile;
  const hasProfilePhoto = Boolean(profileImagePreview || profileImageName || savedProfileImageUrl);
  const hasSavedProfilePhotoUrl = Boolean(savedProfileImageUrl?.trim());
  const [activeClientsCount, setActiveClientsCount] = useState<number | null>(null);
  const [experienceYearsDraft, setExperienceYearsDraft] = useState('');
  const [isExperienceEditing, setIsExperienceEditing] = useState(false);
  const qualificationFieldRef = useRef<HTMLDivElement | null>(null);
  const initials = useMemo(() => getInitials(userName), [userName]);
  const hasProfessionalQualification = formState.primaryQualification.trim().length > 0;
  const professionalQualificationDisplay = useMemo(
    () => formState.primaryQualification.trim() || 'Professional qualification not set',
    [formState.primaryQualification],
  );
  const experienceDisplayValue = useMemo(() => formatExperienceYearsDisplay(experienceYearsDraft), [experienceYearsDraft]);
  const qualificationOptions = useMemo(
    () => getQualificationOptionsForValue(formState.primaryQualification),
    [formState.primaryQualification],
  );
  const registrationExpiryPickerValue = useMemo(() => {
    const parsed = parseRegistrationExpiryToDate(formState.registrationExpiry);
    return parsed ? dayjs(parsed) : null;
  }, [formState.registrationExpiry]);

  const updateField = <K extends keyof ProfileDetailsFormState,>(field: K, value: ProfileDetailsFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const updatePracticeField = <K extends keyof PracticeInfoFormState,>(field: K, value: PracticeInfoFormState[K]) => {
    setPracticeFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleQualificationPromptClick = useCallback(() => {
    const fieldRoot = qualificationFieldRef.current;
    if (!fieldRoot) return;

    fieldRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      const focusTarget = fieldRoot.querySelector<HTMLElement>('[role="combobox"], input, button');
      focusTarget?.focus();
    }, 350);
  }, []);

  const handleQualificationPromptKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleQualificationPromptClick();
      }
    },
    [handleQualificationPromptClick],
  );

  const handleCountryChange = (countryValue: string) => {
    const phoneCode = getPhoneCodeForCountry(countryValue);
    setFormState((prev) => ({
      ...prev,
      country: countryValue,
      phoneCode: phoneCode || prev.phoneCode,
    }));
  };

  const applyProfilePreferences = useCallback((profilePreferences: NutritionistProfilePreferences, profileData?: unknown) => {
    const responseRoot = asRecord(profilePreferences) || {};
    const profileRoot = asRecord(responseRoot.data) || responseRoot;
    const profileDataRoot = asRecord(profileData) || {};
    const resolvedProfileData = asRecord(profileDataRoot.data) || profileDataRoot;
    const personalDetails = getNestedRecord(profileRoot, ['personalDetails', 'personal', 'profile']) || profileRoot;
    const contactInfo = getNestedRecord(profileRoot, ['contactInfo', 'contact']) || profileRoot;
    const credentialsBlock = asRecord(profileRoot.credentials);
    const professionalDetails =
      getNestedRecord(profileRoot, ['credentials', 'professionalDetails', 'professional', 'credentialsAndSpecialisations'])
      || profileRoot;
    const professionalBio = getNestedRecord(profileRoot, ['professionalBio']) || professionalDetails;

    const registrationBodyCandidate = credentialsBlock?.registrationBody ?? professionalDetails.registrationBody;
    const registrationBodyForForm =
      registrationBodyCandidate === null || registrationBodyCandidate === undefined
        ? null
        : typeof registrationBodyCandidate === 'string'
          ? registrationBodyCandidate
          : null;

    const firstName = readString(personalDetails.firstName, profileRoot.firstName);
    const lastName = readString(personalDetails.lastName, profileRoot.lastName);
    const displayName = readString(personalDetails.displayName, profileRoot.displayName);
    const country = normalizeCountryValue(readString(contactInfo.country, profileRoot.country));
    const explicitPhoneCode = readString(
      contactInfo.phoneCountryCode,
      contactInfo.phone_country_code,
      profileRoot.phoneCountryCode,
      profileRoot.phone_country_code,
    );
    const countryPhoneCode = country ? getPhoneCodeForCountry(country) : null;
    const nextFormState: ProfileDetailsFormState = {
      firstName,
      lastName,
      displayName,
      pronouns: readString(personalDetails.pronouns, profileRoot.pronouns),
      email: readString(contactInfo.email, profileRoot.email, initialUser?.email),
      phoneCode: normalizePhoneCode(explicitPhoneCode || countryPhoneCode || readString(initialUser?.phoneCountryCode, '+91')),
      phoneNumber: readString(contactInfo.phone, contactInfo.phoneNumber, profileRoot.phone, initialUser?.phone),
      country,
      zipCode: readString(contactInfo.zipCode, contactInfo.postalCode, profileRoot.zipCode, profileRoot.postalCode),
      linkedInAccount: readString(
        contactInfo.linkedInAccount,
        contactInfo.linked_in_account,
        profileRoot.linkedInAccount,
        profileRoot.linked_in_account,
      ),
      instagramAccount: readString(
        contactInfo.instagramAccount,
        contactInfo.instagram_account,
        profileRoot.instagramAccount,
        profileRoot.instagram_account,
      ),
      professionalBio: readString(professionalBio.bio, professionalDetails.professionalBio, professionalDetails.bio, profileRoot.professionalBio, profileRoot.bio),
      primaryQualification: readString(
        professionalDetails.primaryQualification,
        professionalDetails.qualification,
        profileRoot.primaryQualification,
        profileRoot.qualification
      ),
      specialization: readSpecialization(professionalDetails.specializations, professionalDetails.specialization, professionalDetails.specialisation, profileRoot.specializations, profileRoot.specialization),
      registrationNumber: readString(professionalDetails.registrationNumber, profileRoot.registrationNumber),
      registrationExpiry: normalizeRegistrationExpiryForForm(readString(
        professionalDetails.registrationExpiry,
        professionalDetails.registrationExpiryDate,
        profileRoot.registrationExpiry,
        profileRoot.registrationExpiryDate
      )),
      registrationBody: registrationBodyForForm,
    };

    const resolvedName = getNameFromForm(nextFormState) || initialUser?.fullName?.trim() || initialUser?.email?.split('@')?.[0] || 'User';
    const imageUrl = isProfilePhotoRemoved
      ? ''
      : readString(
          profileRoot.profileImageUrl,
          personalDetails.profileImageUrl,
          resolvedProfileData.profileImageUrl,
          initialUser?.profileImageUrl,
        );
    const experienceYears = readNumber(
      credentialsBlock?.experienceYears,
      profileRoot.experienceYears,
      professionalDetails.experienceYears,
    );

    setFormState(nextFormState);
    setUserName(resolvedName);
    setSavedProfileImageUrl(imageUrl);
    setProfileImagePreview(null);
    setProfileImageName('');
    setSelectedProfileImageFile(null);

    setExperienceYearsDraft(experienceYears !== null ? String(experienceYears) : '');
    setIsExperienceEditing(false);

    updateStoredUserProfile({
      fullName: resolvedName,
      email: nextFormState.email || initialUser?.email,
      phone: nextFormState.phoneNumber,
      phoneCountryCode: nextFormState.phoneCode,
      country: getCountryForApi(nextFormState.country),
      profileImageUrl: imageUrl || undefined,
    });
  }, [initialUser, isProfilePhotoRemoved]);

  const applyPracticeInfo = useCallback((data: Awaited<ReturnType<typeof getNutritionistPracticeInfo>>) => {
    setPracticeFormState({
      practiceName: data.practiceName || '',
    });
    setPracticePersistedFields({
      practiceAddress: data.practiceAddress || '',
      timezone: data.timezone || '',
      country: data.country || '',
      zipCode: data.zipCode || '',
    });
    setSavedLogoUrl(data.clinicLogoUrl || '');
    setLogoPreview(data.clinicLogoUrl || null);
    setLogoFileName('');
    setSelectedLogoFile(null);
  }, []);

  const loadPageData = useCallback(async (silent = false) => {
    if (!silent) {
      setIsPageLoading(true);
    }

    try {
      const [profilePreferences, practiceData, dashboardResult] = await Promise.all([
        getNutritionistProfilePreferences(),
        getNutritionistPracticeInfo(),
        getClientsDashboard({}).catch((error) => {
          console.error('Failed to load active clients count:', error);
          return null;
        }),
      ]);

      applyProfilePreferences(profilePreferences, practiceData);
      applyPracticeInfo(practiceData);

      if (dashboardResult) {
        const activeCount = Number(dashboardResult.counts?.active ?? 0);
        setActiveClientsCount(Number.isFinite(activeCount) ? activeCount : 0);
      }
    } catch (error) {
      console.error('Failed to load profile and practice details:', error);
      if (!silent) {
        showErrorToast(error, 'Failed to load profile');
      }
    } finally {
      if (!silent) {
        setIsPageLoading(false);
      }
    }
  }, [applyPracticeInfo, applyProfilePreferences]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  const handleProfileImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    const allowedMimeTypes = ['image/jpeg', 'image/png'];
    const hasAllowedExtension = /\.(jpe?g|png)$/i.test(file.name);
    if (!allowedMimeTypes.includes(file.type.toLowerCase()) && !hasAllowedExtension) {
      showWarningToast('Unsupported file type', 'Please upload JPG or PNG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showWarningToast('File too large', 'Please select an image under 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setIsProfilePhotoRemoved(false);
      setSelectedProfileImageFile(file);
      setProfileImagePreview(reader.result as string);
      setProfileImageName(file.name);
      showSuccessToast('Photo selected', 'Your new profile photo is ready to be saved.');
    };
    reader.readAsDataURL(file);
  };

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    const allowedMimeTypes = ['image/jpeg', 'image/png'];
    const hasAllowedExtension = /\.(jpe?g|png)$/i.test(file.name);
    if (!allowedMimeTypes.includes(file.type.toLowerCase()) && !hasAllowedExtension) {
      showWarningToast('Unsupported file type', 'Please upload JPG or PNG.');
      return;
    }

    if (file.size > MAX_CLINIC_LOGO_FILE_BYTES) {
      showWarningToast('File too large', 'Please select a clinic logo under 4.49MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedLogoFile(file);
      setLogoPreview(reader.result as string);
      setLogoFileName(file.name);
      showSuccessToast('Logo selected', 'Your logo is ready to be saved.');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = async () => {
    if (selectedProfileImageFile || profileImagePreview) {
      setProfileImagePreview(null);
      setProfileImageName('');
      setSelectedProfileImageFile(null);
      showSuccessToast('Photo selection removed', 'Your saved profile photo has not been changed.');
      return;
    }

    if (!savedProfileImageUrl) return;

    setIsUploadingPhoto(true);
    try {
      await deleteNutritionistProfilePhoto();
      setSavedProfileImageUrl('');
      setProfileImageName('');
      setSelectedProfileImageFile(null);
      setProfileImagePreview(null);
      setIsProfilePhotoRemoved(true);
      updateStoredUserProfile({
        profileImageUrl: undefined,
      });
      showSuccessToast('Photo removed', 'Your profile photo has been removed.');
    } catch (error) {
      console.error('Failed to remove profile photo:', error);
      showErrorToast(error, 'Failed to remove profile photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const buildProfilePayload = (): NutritionistProfilePreferencesPayload => {
    const experienceYearsValue = parseExperienceYearsForApi(experienceYearsDraft) ?? null;
    return {
      ...(isProfilePhotoRemoved ? { profileImageUrl: null } : {}),
      personalDetails: {
        firstName: formState.firstName.trim(),
        lastName: formState.lastName.trim(),
        displayName: formState.displayName.trim(),
        pronouns: formState.pronouns.trim(),
      },
      contactInfo: {
        phone: formState.phoneNumber.trim(),
        phoneCountryCode: formState.phoneCode.trim(),
        country: getCountryForApi(formState.country),
        zipCode: formState.zipCode.trim(),
        linkedInAccount: toNullableContactField(formState.linkedInAccount),
        instagramAccount: toNullableContactField(formState.instagramAccount),
      },
      professionalBio: {
        bio: formState.professionalBio.trim(),
      },
      credentials: {
        primaryQualification: formState.primaryQualification.trim(),
        registrationBody: formState.registrationBody,
        registrationNumber: formState.registrationNumber.trim(),
        registrationExpiry: normalizeRegistrationExpiryForApi(formState.registrationExpiry),
        experienceYears: experienceYearsValue,
        specializations: toSpecializationsPayload(formState.specialization),
      },
    };
  };

  const handleSaveProfile = async () => {
    if (isSavingProfile) return;

    const missingMandatory = getMissingMandatoryProfileDetailFields(formState);
    if (missingMandatory.length > 0) {
      showWarningToast(
        'Fields are required',
        `Please fill in: ${missingMandatory.join(', ')}. Pronouns, professional bio, credentials, experience years, and clinic logo are optional.`,
      );
      return;
    }

    const socialFieldErrors = getSocialAccountFieldErrors(formState);
    if (socialFieldErrors.length > 0) {
      showWarningToast('Invalid social links', socialFieldErrors.join(' '));
      return;
    }

    setIsSavingProfile(true);

    try {
      await saveNutritionistProfilePreferences(buildProfilePayload());

      let nextProfileImageUrl = isProfilePhotoRemoved ? '' : savedProfileImageUrl;
      if (selectedProfileImageFile) {
        setIsUploadingPhoto(true);
        nextProfileImageUrl = await uploadNutritionistProfilePhoto(selectedProfileImageFile);
        setSavedProfileImageUrl(nextProfileImageUrl);
        setIsProfilePhotoRemoved(false);
      }

      const contactCountryForApi = getCountryForApi(formState.country);
      const currencyForCountry = getCurrencyForCountry(contactCountryForApi);

      await updateNutritionistPracticeInfo({
        practiceName: practiceFormState.practiceName.trim(),
        practiceAddress: practicePersistedFields.practiceAddress,
        timezone: practicePersistedFields.timezone,
        country: contactCountryForApi,
        zipCode: practicePersistedFields.zipCode,
        clinicLogoUrl: savedLogoUrl || undefined,
        currency: currencyForCountry || undefined,
      });

      if (selectedLogoFile) {
        const nextLogoUrl = await uploadNutritionistClinicLogo(selectedLogoFile);
        setSavedLogoUrl(nextLogoUrl);
        setLogoPreview(nextLogoUrl || null);
        setLogoFileName('');
        setSelectedLogoFile(null);
      }

      const nextUserName = getNameFromForm(formState) || userName;
      setUserName(nextUserName);
      updateStoredUserProfile({
        fullName: nextUserName,
        email: formState.email.trim(),
        phone: formState.phoneNumber.trim(),
        phoneCountryCode: formState.phoneCode.trim(),
        country: getCountryForApi(formState.country),
        profileImageUrl: nextProfileImageUrl || undefined,
        practiceName: practiceFormState.practiceName.trim(),
        currency: currencyForCountry || undefined,
      });

      setProfileImagePreview(null);
      setProfileImageName('');
      setSelectedProfileImageFile(null);
      showSuccessToast('Profile saved', 'Your profile and practice details have been updated.');
      await loadPageData(true);
    } catch (error) {
      console.error('Failed to save profile:', error);
      showErrorToast(error, 'Failed to save profile');
    } finally {
      setIsUploadingPhoto(false);
      setIsSavingProfile(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Profile and Practice Information
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.5 }, pb: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 }, display: 'grid', gap: 2.25 }}>
          {isPageLoading ? (
            <Box
              sx={{
                minHeight: { xs: 360, sm: 420 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} sx={{ color: '#20AEDC' }} />
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                Loading profile and practice details...
              </Typography>
            </Box>
          ) : (
            <>
          <Box className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#18B5DF] to-[#1A92C6] px-4 py-4 sm:px-6 sm:py-5">
            <div className="pointer-events-none absolute inset-0 z-0 flex items-stretch justify-end opacity-90">
              <img
                src={layer1Graphic}
                alt=""
                aria-hidden
                className="h-full w-[220px] object-cover object-right sm:w-[260px] md:w-[297px]"
              />
            </div>

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <IconButton
                    aria-label="Back to settings"
                    onClick={() => navigate('/profile?view=hub')}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.5)',
                      color: '#fff',
                      p: 0,
                    }}
                  >
                    <ArrowLeft size={16} />
                  </IconButton>
                  <Avatar
                    src={profileImagePreview || savedProfileImageUrl || undefined}
                    alt={userName}
                    sx={{
                      width: 56,
                      height: 56,
                      border: '2px solid rgba(255,255,255,0.6)',
                      bgcolor: '#fff',
                      color: '#1C99C9',
                      fontWeight: 700,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.56rem' }, lineHeight: 1.1 }} noWrap>
                      {userName}
                    </Typography>
                    <Typography
                      role={hasProfessionalQualification ? undefined : 'button'}
                      tabIndex={hasProfessionalQualification ? undefined : 0}
                      onClick={hasProfessionalQualification ? undefined : handleQualificationPromptClick}
                      onKeyDown={hasProfessionalQualification ? undefined : handleQualificationPromptKeyDown}
                      sx={{
                        color: 'rgba(255,255,255,0.88)',
                        fontSize: { xs: '0.75rem', sm: '0.77rem' },
                        cursor: hasProfessionalQualification ? 'default' : 'pointer',
                        borderRadius: '4px',
                        outline: 'none',
                        '&:hover': hasProfessionalQualification ? undefined : { textDecoration: 'underline', textUnderlineOffset: '3px' },
                        '&:focus-visible': hasProfessionalQualification ? undefined : { boxShadow: '0 0 0 2px rgba(255,255,255,0.85)' },
                      }}
                    >
                      {professionalQualificationDisplay}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => document.getElementById('profile-photo-upload')?.click()}
                    disabled={isPageBusy}
                    sx={{
                      minWidth: 112,
                      height: 36,
                      borderRadius: '11px',
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#fff',
                      color: '#1F2B34',
                      '&:hover': {
                        bgcolor: '#f2f7fa',
                      },
                    }}
                  >
                    {isUploadingPhoto ? (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading
                      </Box>
                    ) : (
                      hasSavedProfilePhotoUrl ? 'Update Photo' : 'Upload Photo'
                    )}
                  </Button>
                  <input id="profile-photo-upload" type="file" accept="image/png,image/jpeg" hidden onChange={handleProfileImageSelect} />

                  {hasProfilePhoto ? (
                    <Button
                      variant="outlined"
                      onClick={handleRemoveImage}
                      disabled={isPageBusy}
                      sx={{
                        minWidth: 84,
                        height: 36,
                        borderRadius: '11px',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.7)',
                        color: '#fff',
                        '&:hover': {
                          borderColor: '#fff',
                          bgcolor: 'rgba(255,255,255,0.08)',
                        },
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Box>
              </Box>

              {profileImageName ? (
                <Typography sx={{ mt: 0.9, color: 'rgba(255,255,255,0.9)', fontSize: '0.76rem' }}>{profileImageName}</Typography>
              ) : null}

              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0.84 }}>
                <StatsCard label="Active Clients" value={activeClientsCount === null ? '--' : String(activeClientsCount)} />
                <Box
                  sx={{
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    px: { xs: 1.05, sm: 1.4 },
                    py: 1.05,
                    minHeight: 46,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 0.35,
                  }}
                >
                  {!isExperienceEditing ? (
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label="Edit EXPERIENCE IN YEARS"
                      onClick={() => setIsExperienceEditing(true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setIsExperienceEditing(true);
                        }
                      }}
                      sx={{
                        outline: 'none',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        '&:focus-visible': { boxShadow: '0 0 0 2px rgba(255,255,255,0.85)' },
                      }}
                    >
                      <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.75rem' }, letterSpacing: '0.02em' }}>
                        EXPERIENCE IN YEARS
                      </Typography>
                      {experienceDisplayValue ? (
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.33rem', lineHeight: 1 }}>{experienceDisplayValue}</Typography>
                      ) : null}
                      {experienceYearsDraft.trim() === '' ? (
                        <Typography sx={{ mt: 0.35, color: 'rgba(255,255,255,0.78)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                          Click to enter a number
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.75rem' }, letterSpacing: '0.02em' }}>
                        EXPERIENCE IN YEARS
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignSelf: 'flex-start',
                          width: '100%',
                          maxWidth: '100%',
                        }}
                      >
                        <InputBase
                          autoFocus
                          type="number"
                          disabled={isPageBusy}
                          value={experienceYearsDraft}
                          onChange={(event) => {
                            const next = event.target.value;
                            if (next === '' || /^\d+$/.test(next)) {
                              setExperienceYearsDraft(next);
                            }
                          }}
                          onBlur={() => setIsExperienceEditing(false)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              setIsExperienceEditing(false);
                            }
                          }}
                          placeholder=""
                          inputProps={{
                            min: 0,
                            inputMode: 'numeric',
                            'aria-label': 'EXPERIENCE IN YEARS',
                          }}
                          sx={{
                            flex: '1 1 auto',
                            minWidth: '2ch',
                            '& .MuiInputBase-input': {
                              padding: 0,
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '1.33rem',
                              lineHeight: 1,
                              '&::placeholder': {
                                color: 'rgba(255,255,255,0.45)',
                                opacity: 1,
                              },
                            },
                          }}
                        />
                        <Typography component="span" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '1.33rem', lineHeight: 1, userSelect: 'none' }}>
                          {experienceYearsDraft.trim() === ''
                            ? ''
                            : Number.parseInt(experienceYearsDraft, 10) === 1
                              ? 'Year'
                              : 'Years'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSaveProfile();
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
          >
          <ProfileSection title="Personal Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.65 }}>
              <AppOutlinedTextField
                fullWidth
                required
                disabled={isPageBusy}
                label="First name"
                placeholder={FIELD_PLACEHOLDERS.firstName}
                value={formState.firstName}
                onChange={(event) => updateField('firstName', event.target.value)}
              />
              <AppOutlinedTextField
                fullWidth
                required
                disabled={isPageBusy}
                label="Last name"
                placeholder={FIELD_PLACEHOLDERS.lastName}
                value={formState.lastName}
                onChange={(event) => updateField('lastName', event.target.value)}
              />
              <AppOutlinedTextField
                fullWidth
                required
                disabled={isPageBusy}
                label="Display name"
                placeholder={FIELD_PLACEHOLDERS.displayName}
                value={formState.displayName}
                onChange={(event) => updateField('displayName', event.target.value)}
              />
              <AppOutlinedTextField
                select
                fullWidth
                disabled={isPageBusy}
                label="Pronouns"
                value={formState.pronouns}
                onChange={(event) => updateField('pronouns', event.target.value)}
              >
                <MenuItem value="">
                  Select pronouns
                </MenuItem>
                {PRONOUN_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </AppOutlinedTextField>
            </Box>

            <Typography sx={{ mt: 2.1, mb: 2, color: '#1F2B34', fontWeight: 700, fontSize: SECTION_HEADER_FONT_SIZE }}>
              Contact Information
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.65 }}>
              <AppOutlinedTextField
                fullWidth
                required
                label="Email"
                placeholder={FIELD_PLACEHOLDERS.email}
                value={formState.email}
                InputProps={{ readOnly: true }}
              />
              <AppOutlinedTextField
                fullWidth
                required
                disabled={isPageBusy}
                type="tel"
                label="Phone number"
                placeholder={FIELD_PLACEHOLDERS.phoneNumber}
                value={formState.phoneNumber}
                onChange={(event) => updateField('phoneNumber', event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        color: '#94A3B8',
                        fontSize: '0.9rem',
                        alignSelf: 'center',
                        mt: 0,
                        mr: 1,
                      }}
                    >
                      {formState.phoneCode}
                    </InputAdornment>
                  ),
                  sx: {
                    alignItems: 'center',
                  },
                }}
              />
              <AppOutlinedTextField
                select
                fullWidth
                required
                disabled={isPageBusy}
                shrinkLabel={Boolean(formState.country)}
                label="Country"
                value={formState.country}
                onChange={(event) => handleCountryChange(event.target.value)}
              >
                <MenuItem value="" disabled>
                  Select country
                </MenuItem>
                {COUNTRY_OPTIONS.map((countryOption) => (
                  <MenuItem key={countryOption.value} value={countryOption.value}>
                    {countryOption.label}
                  </MenuItem>
                ))}
              </AppOutlinedTextField>
              <AppOutlinedTextField
                fullWidth
                required
                disabled={isPageBusy}
                type="text"
                label="Zip code"
                placeholder={FIELD_PLACEHOLDERS.zipCode}
                value={formState.zipCode}
                onChange={(event) => updateField('zipCode', event.target.value)}
              />
              <AppOutlinedTextField
                fullWidth
                disabled={isPageBusy}
                type="text"
                label="LinkedIn"
                placeholder={FIELD_PLACEHOLDERS.linkedInAccount}
                value={formState.linkedInAccount}
                onChange={(event) => updateField('linkedInAccount', event.target.value)}
                inputProps={{ maxLength: SOCIAL_ACCOUNT_MAX_LENGTH }}
              />
              <AppOutlinedTextField
                fullWidth
                disabled={isPageBusy}
                type="text"
                label="Instagram"
                placeholder={FIELD_PLACEHOLDERS.instagramAccount}
                value={formState.instagramAccount}
                onChange={(event) => updateField('instagramAccount', event.target.value)}
                inputProps={{ maxLength: SOCIAL_ACCOUNT_MAX_LENGTH }}
              />
            </Box>

            <Box sx={{ mt: 2.15 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.35 }}>
                <Typography
                  id="profile-details-professional-bio-heading"
                  sx={{ color: '#1F2B34', fontWeight: 700, fontSize: SECTION_HEADER_FONT_SIZE }}
                >
                  Professional Bio
                </Typography>
              </Box>
              <AppOutlinedTextField
                multiline
                minRows={1}
                maxRows={4}
                fullWidth
                aria-labelledby="profile-details-professional-bio-heading"
                placeholder={FIELD_PLACEHOLDERS.professionalBio}
                value={formState.professionalBio}
                disabled={isPageBusy}
                onChange={(event) => updateField('professionalBio', event.target.value)}
                shrinkLabel={false}
                slotProps={{
                  input: { notched: false },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#F7FAFC',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E2E8F0',
                    },
                    '& fieldset legend': {
                      maxWidth: '0 !important',
                      '& span': {
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                    },
                  },
                  '& .MuiOutlinedInput-root.Mui-focused fieldset legend': {
                    maxWidth: '0 !important',
                  },
                  '& .MuiOutlinedInput-inputMultiline': {
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                    color: '#1F2B34',
                    '&::placeholder': {
                      color: '#94A3B8',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </ProfileSection>

          <ProfileSection title="Credentials and Specializations">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.65 }}>
              <AppOutlinedTextField
                ref={qualificationFieldRef}
                select
                fullWidth
                disabled={isPageBusy}
                label="Professional Qualification"
                placeholder={FIELD_PLACEHOLDERS.primaryQualification}
                value={formState.primaryQualification}
                onChange={(event) => updateField('primaryQualification', event.target.value)}
              >
                <MenuItem value="">
                  Select professional qualification
                </MenuItem>
                {qualificationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </AppOutlinedTextField>
              <AppOutlinedTextField
                fullWidth
                disabled={isPageBusy}
                label="Specialization"
                placeholder={FIELD_PLACEHOLDERS.specialization}
                value={formState.specialization}
                onChange={(event) => updateField('specialization', event.target.value)}
              />
              <AppOutlinedTextField
                fullWidth
                disabled={isPageBusy}
                label="Registration Number"
                placeholder={FIELD_PLACEHOLDERS.registrationNumber}
                value={formState.registrationNumber}
                onChange={(event) => updateField('registrationNumber', event.target.value)}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Reg. Expiry"
                  format="D MMMM YYYY"
                  value={registrationExpiryPickerValue}
                  disabled={isPageBusy}
                  onChange={(newValue) => {
                    updateField(
                      'registrationExpiry',
                      newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '',
                    );
                  }}
                  desktopModeMediaQuery="@media (max-width: 0px)"
                  slotProps={{
                    dialog: {
                      sx: {
                        '& .MuiDialog-container': {
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                      },
                    },
                    textField: {
                      fullWidth: true,
                      sx: appOutlinedTextFieldSx,
                      slotProps: { inputLabel: { shrink: true } },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </ProfileSection>

          <ProfileSection title="Practice Information">
            <AppOutlinedTextField
              fullWidth
              disabled={isPageBusy}
              label="Practice / Clinic Name"
              placeholder={FIELD_PLACEHOLDERS.practiceName}
              value={practiceFormState.practiceName}
              onChange={(event) => updatePracticeField('practiceName', event.target.value)}
            />

            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: SECTION_HEADER_FONT_SIZE, mb: 1.35 }}>
                Upload Logo
              </Typography>

              <Box
                sx={{
                  border: '1px dashed #cfd9e3',
                  borderRadius: '10px',
                  bgcolor: '#F7FAFC',
                  py: { xs: 3, sm: 3.5 },
                  px: 2,
                  textAlign: 'center',
                }}
              >
                {logoPreview ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Practice logo preview"
                      referrerPolicy="no-referrer"
                      sx={{
                        maxHeight: 100,
                        maxWidth: 220,
                        width: 'auto',
                        borderRadius: '8px',
                        border: '1px solid #dfe7ee',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : null}

                <Typography sx={{ color: '#94A3B8', fontSize: '0.82rem', lineHeight: 1.4 }}>
                  Upload JPG, PNG (max 5MB)
                </Typography>
                <Typography sx={{ color: '#94A3B8', fontSize: '0.82rem', mb: 1.6 }}>for best quality</Typography>

                <Button
                  type="button"
                  variant="outlined"
                  disabled={isPageBusy}
                  onClick={() => document.getElementById('practice-logo-upload')?.click()}
                  sx={{
                    borderRadius: '11px',
                    minWidth: 132,
                    height: 40,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#475569',
                    borderColor: '#CBD5E1',
                    '&:hover': {
                      borderColor: '#94A3B8',
                      bgcolor: '#F8FAFC',
                    },
                  }}
                >
                  Choose File
                </Button>
                <input
                  id="practice-logo-upload"
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={isPageBusy}
                  hidden
                  onChange={handleLogoSelect}
                />

                {logoFileName ? (
                  <Typography sx={{ mt: 1.25, color: '#64748b', fontSize: '0.8rem' }}>{logoFileName}</Typography>
                ) : null}
              </Box>
            </Box>
          </ProfileSection>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              type="button"
              variant="outlined"
              disabled={isPageBusy}
              onClick={() => navigate('/profile?view=hub')}
              sx={{
                minWidth: 132,
                height: 40,
                borderRadius: '11px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#CBD5E1',
                color: '#475569',
                '&:hover': {
                  borderColor: '#94A3B8',
                  bgcolor: '#F8FAFC',
                },
              }}
            >
              Back 
            </Button>
            <Button
              variant="contained"
              disableElevation
              type="submit"
              disabled={isPageBusy}
              sx={{
                minWidth: 132,
                height: 40,
                borderRadius: '11px',
                bgcolor: '#20AEDC',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#168FBA' },
              }}
            >
              {isSavingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
          </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProfileDetailsView;
